import { useCallback, useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import { problem } from './util.js';

// In a booking: the stock the engineers logged when they completed the job.
// The office can correct it once the job is completed (the engineers can't);
// each correction goes in the booking's history.
export default function JobStock({ booking, canEdit, onSaved }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // itemId -> typed amount
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api(`/job-stock/${booking.id}`).then(setData, (e) => setError(problem(e)));
  }, [booking.id]);
  useEffect(() => { load(); }, [load, booking.status]);

  if (!data || (booking.status !== 'completed' && !data.lines.length)) return null;

  const start = () => setEditing(Object.fromEntries(data.lines.map((l) => [l.itemId, String(l.qty)])));
  // The items that can be logged, plus anything already on the job that has
  // since been switched off.
  const items = [...data.items, ...data.lines.filter((l) => !data.items.some((i) => i.id === l.itemId)).map((l) => ({ id: l.itemId, sku: l.sku, description: l.description }))];
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/job-stock/${booking.id}`, { method: 'PUT', body: { lines: items.map((i) => ({ itemId: i.id, qty: Number(editing[i.id]) || 0 })) } });
      setEditing(null);
      load();
      onSaved?.();
    } catch (e) {
      setError(problem(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="job-stock-office">
      <h3>Stock used</h3>
      {error && <p className="notice-line notice-bad">{error}</p>}
      {!editing && (
        <>
          {!data.lines.length && <p className="muted small">None logged.</p>}
          {data.lines.length > 0 && (
            <>
              <table className="table">
                <thead><tr><th>Item</th><th>SKU</th><th className="num">Used</th></tr></thead>
                <tbody>{data.lines.map((l) => <tr key={l.itemId}><td>{l.description}</td><td className="small muted">{l.sku}</td><td className="num"><strong>{l.qty}</strong></td></tr>)}</tbody>
              </table>
              <p className="small muted">Logged by {data.lines[0].loggedByName}, {formatDateTime(data.lines[0].loggedAt)}.</p>
            </>
          )}
          {canEdit && booking.status === 'completed' && <button type="button" className="btn with-icon" onClick={start}><Pencil size={16} aria-hidden="true" /> Correct stock</button>}
        </>
      )}
      {editing && (
        <>
          <p className="small muted">Correct what was used. The change is recorded in the booking’s history.</p>
          <div className="job-stock-edit">
            {items.map((i) => (
              <label key={i.id} className="fld">
                <span>{i.description} <span className="small muted">{i.sku}</span></span>
                <input inputMode="numeric" pattern="[0-9]*" value={editing[i.id] ?? ''} placeholder="0"
                  onChange={(e) => setEditing({ ...editing, [i.id]: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
              </label>
            ))}
          </div>
          <div className="actions">
            <button type="button" className="btn primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
